// Mostly the same as addItem, but kept separate so custom shirt designs can be added later.
import {
  loadSVGFromURL,
  util,
  Group,
  FabricText,
  type FabricObject,
  type Canvas,
} from 'fabric';
import { isDugout } from '@/config/features';
import { ALTERNATE_ICON_COLOR } from '@/config/dugout';

export interface AddShirtProps {
  name: string;
  src: string;
  left: number;
  top: number;
  variation?: string;
  canvas: Canvas;
  width: number | string | undefined;
  height: number | string | undefined;
  defaultIcon?: unknown;
  alternateIcon?: unknown;
  fontColor?: string;
  scaleFactor: number;
  // The resolved theme (config/theme.ts). Passed in because these helpers run outside React — reading
  // process.env here would pin them to the BUILD theme.
  theme: string;
  color?: string;
  iconType?: string;
  // Dugout-only: the number-text seeded at creation (rinus starts empty, filled via the Editor) and
  // its offset from the item origin. Absent for every other theme — see the gated fork below.
  text?: string;
  fontTop?: number;
  fontLeft?: number;
}

let itemCount = 1;

const addShirt = (props: AddShirtProps): void => {
  const {
    name,
    src,
    left,
    top,
    variation,
    canvas,
    width,
    height,
    defaultIcon,
    alternateIcon,
    fontColor,
    scaleFactor,
    iconType,
    color,
    text: initialText,
    fontTop,
    fontLeft,
  } = props;

  void loadSVGFromURL(src).then(({ objects, options }) => {
    const parsed = objects.filter(
      (object): object is FabricObject => object !== null,
    );

    // Dugout ships one shirt SVG per shape and recolours it per team when the item is created (rinus
    // uses pre-coloured files, so this is gated off for every other theme). `defaultIcon` mode paints
    // the main shape the team `color`; `alternateIcon` (symbol) mode paints it one neutral grey.
    if (isDugout(props.theme) && parsed[0]) {
      parsed[0].set({
        fill:
          iconType === 'defaultIcon' && color ? color : ALTERNATE_ICON_COLOR,
      });
    }

    const svgData = util.groupSVGElements(parsed, options);

    svgData.name = name;

    // sourcePath tells the import/export to use this path, instead of embedding the code in the JSON
    svgData.set({ left, top, sourcePath: src });
    svgData.scaleToWidth(Number(width));
    svgData.scaleToHeight(Number(height));

    // only shirts have the text element

    const numericHeight = Number(height);
    const numericWidth = Number(width);
    const offsetTop = parseInt(String(top)) + numericHeight / 2;
    const offsetLeft = parseInt(String(left)) + numericWidth / 2;

    const offsetTopForShirts = offsetTop - 5;
    const offsetLeftForShirts = offsetLeft - 4;

    // Dugout seeds the jersey number at creation (formations carry numbers; rinus starts empty and
    // fills it via the Editor) and draws it in Helvetica/9 nudged by the item's fontTop/fontLeft —
    // matching the Dugout tool. Gated so every other theme keeps the exact empty-string / sans-serif /
    // 11px text object and its offset maths (byte-identical rendered output).
    const text = isDugout(props.theme)
      ? new FabricText(initialText ?? '', {
          originLeft: ((left + offsetLeft) / scaleFactor) * 1,
          originTop: ((top + offsetTop) / scaleFactor) * 1,
          // Centre the number on the shape (offsetLeft/offsetTop = shape centre) then apply Dugout's
          // small per-item nudge, instead of rinus's fixed -4/-5 shirt offset.
          top: offsetTop + (fontTop ?? 0),
          left: offsetLeft + (fontLeft ?? 0),
          originX: 'center',
          originY: 'center',
          scaleX: scaleFactor,
          scaleY: scaleFactor,
          fill: fontColor,
          fontFamily: 'Helvetica',
          fontSize: variation === 'shirt-annotation' ? 14 : 9,
        })
      : new FabricText('', {
          originLeft: ((left + offsetLeft) / scaleFactor) * 1,
          originTop: ((top + offsetTop) / scaleFactor) * 1,
          top:
            variation === 'shirt-annotation' ? offsetTop : offsetTopForShirts,
          left:
            variation === 'shirt-annotation' ? offsetLeft : offsetLeftForShirts,
          originX: 'center',
          originY: 'center',
          scaleX: scaleFactor,
          scaleY: scaleFactor,
          fill: fontColor,
          fontFamily: 'sans-serif',
          // TO-DO: Change shirt-text variation name to annotation-something
          fontSize: variation === 'shirt-annotation' ? 14 : 11,
        });

    // group the svgGroup and text items into the same group (so they're always dragged together)
    const group = new Group([svgData, text], {
      id: `shirt-${itemCount}`,
      name,
      originLeft: (left / scaleFactor) * 1,
      originTop: (top / scaleFactor) * 1,
      left,
      top,
      scaleX: scaleFactor,
      scaleY: scaleFactor,
      originX: 'center',
      originY: 'center',
      // images dont need to be stretched, rotated, etc. keep it simple
      hasBorders: true,
      hasControls: true,
      variation,
      defaultIcon,
      alternateIcon,
      // Dugout re-derives an item's colour on shape-toggle/import, so it stores `color` on the group;
      // the other themes never read it back (pre-coloured SVGs), so it stays off their objects.
      ...(isDugout(props.theme) && { color }),
    });

    // eslint-disable-next-line unicorn/no-top-level-assignment-in-function -- module-scoped counter giving each shirt a unique id
    itemCount++;

    canvas.setActiveObject(group);

    canvas.add(group);
  });
};

export default addShirt;
